//dependencies
import React, { PropsWithChildren, useRef } from "react";
import { useHover } from "usehooks-ts";

//components
import Typography from "../../../../components/Text";

//css
import css from "./TagItem.module.scss";

type TagItemProps<T> = {
	/**
	 * The value of the tag item
	 */
	value: T;
	/**
	 * If true, the tag item is disabled
	 */
	disabled?: boolean;
	/**
	 *
	 * function ran on Click
	 */
	onClick?: (value: T) => void;
	/**
	 * If true, the tag item is selected
	 */
	selected?: boolean;
	/**
	 * Aria label for the tag item, this is used for accessibility and needs to be added if the label is not a string
	 */
	ariaLabel?: string;
	/**
	 * If true, the tag item is focused, and has a focus ring
	 */
	focused?: boolean;
	/**
	 * If true, the tag item is animated active state where it drops 1 px
	 */
	animatedActive?: boolean;
};

const TagItem = <T,>({
	children,
	onClick,
	value,
	selected,
	ariaLabel,
	disabled = false,
	focused = false,
	animatedActive = false,
}: PropsWithChildren<TagItemProps<T>>) => {
	const ref = useRef<HTMLDivElement>(null);
	const isHovered = useHover(ref);

	return (
		<div
			id={`tag-item-${JSON.stringify(value)}`}
			data-testid={`tag-item-${ariaLabel || value}`}
			onClick={() => !disabled && onClick && onClick(value)}
			className={` ${css.tagItem} ${selected ? css.selected : null} ${disabled ? css.disabled : null} ${focused ? css.focused : null} ${animatedActive ? css.animatedActive : null} `}
			role="gridcell"
			tabIndex={-1}
			aria-selected={selected}
			aria-label={ariaLabel}
			data-selected={selected}
			aria-disabled={disabled}
			data-focused={focused}
			data-focus-visible={focused}
			data-hovered={isHovered}
			ref={ref}
		>
			{typeof children === "string" ? (
				<Typography color="neutral-7" size="paragraph-3" weight="semi-bold">
					{children}
				</Typography>
			) : (
				children
			)}
		</div>
	);
};

export default TagItem;
